import { Log, type LogStream } from 'bblog';
import { ConsoleLogStream } from 'bblog-stream-console';
import type { IkeConfig, IkeConfigKey } from '../config';
import { IkeLogStreamType } from './log.streams';
import { IkeLogType } from './log.types';

export { IkeLogType } from './log.types';
export { IkeLogStreamType } from './log.streams';

export const LogKeys = {
	CONSOLE: 'console',
	TYPE: 'type',
	STREAM: 'stream',
	LEVEL: 'level',
	CONFIG: 'config',
};

export interface IkeLogOptions {
	name: string;
	hostname: string;
	keys?: { [key: string]: string | number };
}

const REQUIRED_KEYS = [LogKeys.LEVEL, LogKeys.STREAM];

export function LOGKEY(type: IkeLogType, key: string): IkeConfigKey {
	return <IkeConfigKey>['log', type, key].join('.');
}

export { Log, type LogStream, type LogLevel, type LogMessage, type LoggerCreationContext } from 'bblog';

const FACTORIES: { [key: string]: IkeLogStreamFactory } = {};

export interface IkeLogStreamFactory {
	type: IkeLogStreamType;
	create: (level: number, attrs?: any) => LogStream;
}

export class IkeLog extends Log {
	static create(config: IkeConfig, type: IkeLogType, options: IkeLogOptions): Log {
		const requiredKeys = REQUIRED_KEYS.map((k) => LOGKEY(type, k));
		if (config.requires(requiredKeys)) {
			throw new Error(`Unable to create logger ${type} missing keys: ` + config.missing(requiredKeys).join(','));
		}
		const streams: LogStream[] = [];
		const logLevel = config.getDefaultNumber(LOGKEY(type, LogKeys.LEVEL), 10);
		const logStreamType = config.getString(LOGKEY(type, LogKeys.STREAM));
		const consoleEnabled = LOGKEY(type, LogKeys.CONSOLE);

		if (config.getBoolean(consoleEnabled) && logStreamType !== IkeLogStreamType.CONSOLE) {
			streams.push(ConsoleStreamFactory.create(logLevel));
		}

		if (logStreamType != null) {
			const configKey = LOGKEY(type, LogKeys.CONFIG);
			const logConfig = config.get(configKey);
			const factory = FACTORIES[logStreamType];
			if (factory == null) {
				throw new Error(
					`Unable to construct log stream: ${logStreamType} please import @ikegps/core-log-${logStreamType}`
				);
			}
			const logStream = factory.create(logLevel, logConfig);
			streams.push(logStream);
		}

		const logger: IkeLog = IkeLog.createLogger({
			name: options.name,
			streams,
			hostname: options.hostname,
			keys: options.keys,
		});

		config.register(logger);

		return logger;
	}

	static register(factory: IkeLogStreamFactory) {
		FACTORIES[factory.type] = factory;
	}
}

const ConsoleStreamFactory: IkeLogStreamFactory = {
	type: IkeLogStreamType.CONSOLE,
	create: (level) => new ConsoleLogStream(level),
};

IkeLog.register(ConsoleStreamFactory);
